/**
 * Admin Users Overview — lista userów z postami i leadami.
 * Prosty widok do manualnego reagowania.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Users, ChevronRight, AlertTriangle, CheckCircle2 } from 'lucide-react';
import PanelLayout from '@/components/PanelLayout';
import { API } from '@/utils/api';
import { useTranslation } from 'react-i18next';

export default function AdminUsers() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [journey, setJourney] = useState(null);
  const [showTest, setShowTest] = useState(false);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/users-overview?include_test=${showTest}`, { headers })
      .then(r => setUsers(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [showTest]); // eslint-disable-line

  const loadJourney = async (userId) => {
    if (selectedUser === userId) { setSelectedUser(null); setJourney(null); return; }
    setSelectedUser(userId);
    try {
      const res = await axios.get(`${API}/admin/debug/user/${userId}`, { headers });
      setJourney(res.data);
    } catch { setJourney(null); }
  };

  if (loading) return <PanelLayout><div className="flex items-center justify-center h-64 text-zinc-400">Ładowanie...</div></PanelLayout>;

  const realUsers = users.filter(u => !u.is_demo);
  const totalPosts = users.reduce((s, u) => s + u.posts, 0);
  const totalLeads = users.reduce((s, u) => s + u.leads, 0);

  return (
    <PanelLayout>
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center mb-8">
          <span className="inline-block px-3 py-1 rounded-full bg-zinc-900 text-white text-xs font-semibold mb-3">Admin</span>
          <h1 className="text-2xl font-bold text-zinc-900 mb-1">Użytkownicy</h1>
          <p className="text-sm text-zinc-500">{realUsers.length} realnych użytkowników / {totalPosts} postów / {totalLeads} leadów</p>
          <label className="mt-3 inline-flex items-center gap-2 text-xs text-zinc-500 cursor-pointer select-none" data-testid="admin-show-test-toggle">
            <input
              type="checkbox"
              checked={showTest}
              onChange={e => setShowTest(e.target.checked)}
              className="rounded border-zinc-300 text-[#F47A3B] focus:ring-[#F47A3B]"
            />
            Pokaż konta testowe (demo, QA)
          </label>
        </div>

        <div className="space-y-2">
          {users.map((u, i) => (
            <div key={u.id}>
              <motion.div
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                onClick={() => loadJourney(u.id)}
                className={`bg-white border rounded-2xl p-4 cursor-pointer transition-all hover:border-[#F47A3B]/30 ${
                  selectedUser === u.id ? 'border-[#F47A3B] shadow-md' : 'border-zinc-200'
                } ${u.is_demo ? 'opacity-50' : ''}`}
                data-testid={`user-row-${u.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center shrink-0">
                    <Users className="w-5 h-5 text-zinc-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-[14px] font-semibold text-zinc-800 truncate">{u.name || u.email}</p>
                      {u.is_demo && <span className="text-[10px] bg-zinc-200 text-zinc-500 px-1.5 py-0.5 rounded-full">demo</span>}
                      {u.profile_type && <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${u.profile_type === 'biznes' ? 'bg-blue-50 text-blue-600' : 'bg-emerald-50 text-emerald-600'}`}>{u.profile_type}</span>}
                    </div>
                    <p className="text-[11px] text-zinc-400 truncate">{u.email}</p>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-center">
                      <p className={`text-lg font-bold ${u.posts > 0 ? 'text-emerald-600' : 'text-zinc-300'}`}>{u.posts}</p>
                      <p className="text-[10px] text-zinc-400">posty</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-lg font-bold ${u.leads > 0 ? 'text-[#F47A3B]' : 'text-zinc-300'}`}>{u.leads}</p>
                      <p className="text-[10px] text-zinc-400">leady</p>
                    </div>
                    <div className="text-center min-w-[60px]">
                      {u.last_event ? (
                        <p className="text-[11px] text-zinc-500 font-medium">{u.last_event}</p>
                      ) : (
                        <p className="text-[11px] text-zinc-300">brak</p>
                      )}
                      <p className="text-[10px] text-zinc-400">ostatni</p>
                    </div>
                    <ChevronRight className={`w-4 h-4 text-zinc-300 transition-transform ${selectedUser === u.id ? 'rotate-90' : ''}`} />
                  </div>
                </div>
              </motion.div>

              {selectedUser === u.id && journey && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="bg-zinc-50 border border-zinc-200 border-t-0 rounded-b-2xl p-5 -mt-2"
                  data-testid={`journey-${u.id}`}
                >
                  <p className="text-[13px] font-bold text-zinc-700 mb-3">Journey Debug</p>
                  {journey.dropoff_at && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      <span className="text-[13px] text-red-700 font-medium">Odpada na: <strong>{journey.dropoff_at}</strong></span>
                    </div>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {journey.journey.map(step => (
                      <div key={step.step} className={`rounded-xl p-3 text-center ${step.done ? 'bg-emerald-50 border border-emerald-200' : 'bg-white border border-zinc-200'}`}>
                        {step.done
                          ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto mb-1" />
                          : <div className="w-4 h-4 rounded-full border-2 border-zinc-200 mx-auto mb-1" />
                        }
                        <p className="text-[11px] font-medium text-zinc-700">{step.step}</p>
                        <p className="text-[10px] text-zinc-400">{step.count}x</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex gap-4 text-[12px] text-zinc-500">
                    <span>Posty: <strong>{journey.stats.posts_published}</strong></span>
                    <span>Leady: <strong>{journey.stats.leads}</strong></span>
                    <span>Universal: <strong>{journey.stats.universal_leads}</strong></span>
                  </div>
                </motion.div>
              )}
            </div>
          ))}
        </div>
      </div>
    </PanelLayout>
  );
}
